import { FC, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Select,
  Button,
  Input,
  Icon,
  ContentBlock,
  Tooltip,
} from '@prometheus/components';
import { AnalysisDTOInterface } from '@prometheus/types';
import { FormElement, SecondaryContentWrapper } from '@prometheus/features';

import { isEmpty } from '@prometheus/utils';
import { getDbmsTypes, updateDataSource } from '@/api';
import { parseDbmsTypesForSelectForm } from '@/utils';
import { DBMSTypesDTO } from '@/types';
import { Documents } from '@prometheus/documents';

interface CreateDatabaseFormProps {
  afterCreate: (analysis: any) => void;
  onCancel: () => void;
  createMethod: (data: any) => Promise<any>;
  isUpdate?: boolean;
}

export const CreateDatabaseForm: FC<CreateDatabaseFormProps> = props => {
  const { afterCreate, onCancel, createMethod } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dbsmTypes, setDbsmTypes] = useState([]);
  const [originFile, setOriginFile] = useState();
  const [dbsmTypesDetails, setDbsmTypesDetails] = useState<DBMSTypesDTO>();
  const [state, setState] = useState<any>({
    name: '',
    description: '',
    type: '',
    connectionString: '',
    cdmSchema: '',
    resultSchema: '',
    targetSchema: '',
    published: false,
    credentials: {
      username: '',
      password: '',
    },
  });

  useEffect(() => {
    getDbmsTypes().then((res: DBMSTypesDTO) => {
      setDbsmTypes(parseDbmsTypesForSelectForm(res));
      setDbsmTypesDetails(res);
    });
  }, []);

  const handleSave = useCallback(() => {
    setIsLoading(true);
    let fd = new FormData();
    fd.append(
      'datasource',
      new Blob([JSON.stringify(state)], {
        type: 'application/json',
      })
    );
    fd.append('keyfile', originFile);

    createMethod(fd).then((response: AnalysisDTOInterface) => {
      setIsLoading(false);
      afterCreate?.(response);
    });
  }, [state, createMethod, originFile]);

  const NOT_VALID =
    isEmpty(state.name) ||
    isEmpty(state.type) ||
    isEmpty(state.connectionString) ||
    isEmpty(state.cdmSchema) ||
    isEmpty(state.resultSchema) ||
    (state.type === 'bigquery'
      ? !originFile
      : isEmpty(state.credentials.username) ||
        isEmpty(state.credentials.password));

  return (
    <Grid container>
      <SecondaryContentWrapper>
        <Grid item xs={12}>
          <ContentBlock
            title={t('modules.administration.databases.general_block')}
            collapsible
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormElement name="name" textLabel={t('forms.name')} required>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    size="medium"
                    placeholder={t('forms.name_placeholder')}
                    value={state.name}
                    onChange={(e: any) =>
                      setState({ ...state, name: e.target.value })
                    }
                    fullWidth
                  />
                </FormElement>
              </Grid>
              <Grid item xs={12}>
                <FormElement
                  name="description"
                  textLabel={t('forms.description')}
                >
                  <Input
                    id="description"
                    name="description"
                    type="text"
                    size="medium"
                    placeholder={t('forms.description_placeholder')}
                    value={state.description}
                    onChange={(e: any) =>
                      setState({ ...state, description: e.target.value })
                    }
                    fullWidth
                  />
                </FormElement>
              </Grid>
              <Grid item xs={12}>
                <FormElement
                  name="type"
                  textLabel={t('forms.dbms_type')}
                  required
                >
                  <Select
                    className=""
                    name="type"
                    disablePortal
                    id="type"
                    options={dbsmTypes}
                    value={state.type}
                    placeholder={t('forms.dbms_type_placeholder')}
                    onChange={(type: any) => {
                      setState({ ...state, type });
                    }}
                    fullWidth
                  />
                </FormElement>
              </Grid>
              <Grid item xs={12}>
                <FormElement
                  name="connectionString"
                  textLabel={t('forms.connection_string')}
                  required
                >
                  <Input
                    id="connectionString"
                    name="connectionString"
                    type="text"
                    size="medium"
                    placeholder={t('forms.connection_string_placeholder')}
                    value={state.connectionString}
                    onChange={(e: any) =>
                      setState({ ...state, connectionString: e.target.value })
                    }
                    fullWidth
                  />
                </FormElement>
              </Grid>

              {state.type === 'bigquery' ? (
                <Grid item xs={12}>
                  <Documents
                    type="component"
                    name="import-json-file"
                    config={
                      {
                        titleButton: t('buttons.upload_json'),
                        initialTextFile: undefined,
                        required: true,
                        onChange: (parsedJson: any, file: any) => {
                          setOriginFile(file);
                        },
                      } as any
                    }
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12}>
                    <FormElement
                      name="username"
                      textLabel={t('forms.username')}
                      required
                    >
                      <Input
                        id="username"
                        name="username"
                        type="text"
                        size="medium"
                        placeholder={t('forms.username_placeholder')}
                        value={state?.credentials?.username}
                        onChange={(e: any) =>
                          setState({
                            ...state,
                            credentials: {
                              ...state?.credentials,
                              username: e.target.value,
                            },
                          })
                        }
                        fullWidth
                      />
                    </FormElement>
                  </Grid>
                  <Grid item xs={12}>
                    <FormElement
                      name="password"
                      textLabel={t('forms.password')}
                      required
                    >
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        size="medium"
                        placeholder={t('forms.password_placeholder')}
                        value={state?.credentials?.password}
                        onChange={(e: any) =>
                          setState({
                            ...state,
                            ...state,
                            credentials: {
                              ...state.credentials,
                              password: e.target.value,
                            },
                          })
                        }
                        fullWidth
                      />
                    </FormElement>
                  </Grid>
                </>
              )}
            </Grid>
          </ContentBlock>
        </Grid>
        <Grid item xs={12} mt={2}>
          <ContentBlock
            title={t('modules.administration.databases.cdm_settings')}
            collapsible
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormElement
                  name="cdmSchema"
                  textLabel={t('forms.cdm_schema')}
                  required
                >
                  <Input
                    id="cdmSchema"
                    name="cdmSchema"
                    type="text"
                    size="medium"
                    placeholder={t('forms.cdm_schema_placeholder')}
                    value={state.cdmSchema}
                    onChange={(e: any) =>
                      setState({ ...state, cdmSchema: e.target.value })
                    }
                    fullWidth
                  />
                </FormElement>
              </Grid>
              <Grid item xs={12}>
                <FormElement
                  name="resultSchema"
                  textLabel={t('forms.result_schema')}
                  required
                >
                  <Input
                    id="resultSchema"
                    name="resultSchema"
                    type="text"
                    size="medium"
                    placeholder={t('forms.result_schema_placeholder')}
                    value={state.resultSchema}
                    onChange={(e: any) =>
                      setState({ ...state, resultSchema: e.target.value })
                    }
                    fullWidth
                  />
                </FormElement>
              </Grid>
              <Grid item xs={12}>
                <FormElement name="schema" textLabel={t('forms.schema')}>
                  <Input
                    id="schema"
                    name="schema"
                    type="text"
                    size="medium"
                    placeholder={t('forms.schema_placeholder')}
                    value={state.targetSchema}
                    onChange={(e: any) =>
                      setState({ ...state, targetSchema: e.target.value })
                    }
                    fullWidth
                  />
                </FormElement>
              </Grid>
            </Grid>
          </ContentBlock>
        </Grid>

        <Grid item xs={12} mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            disabled={isLoading}
            onClick={onCancel}
            startIcon={<Icon iconName="deactivate" />}
          >
            {t('buttons.cancel')}
          </Button>
          <Tooltip
            text={
              NOT_VALID
                ? t('messages.error_message_create_database')
                : undefined
            }
          >
            <div>
              <Button
                disabled={isLoading || NOT_VALID}
                onClick={handleSave}
                variant="contained"
                color="success"
                startIcon={<Icon iconName="submit" />}
                sx={{ ml: 2 }}
              >
                {t('buttons.save')}
              </Button>
            </div>
          </Tooltip>
        </Grid>
      </SecondaryContentWrapper>
    </Grid>
  );
};
